import type { FormDialogRef } from '@/components'
import {
  Button,
  Checkbox,
  DatePicker,
  FAIcon,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FormDialog,
  Input,
} from '@/components'
import {
  CREATE_USER_CHILD_MUTATION,
  UPDATE_USER_CHILD_MUTATION,
} from '@/modules/users/mutations'
import { USER_CHILDREN_QUERY } from '@/modules/users/queries'
import { dateFormat } from '@/utils/graphql'
import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import z from 'zod'
import type { UserChildNode } from './absence-childcare'

function useChildcareSchema() {
  const translations = useTranslate({
    name: 'common.required',
  })

  return z.object({
    name: z.string().trim().min(1, translations.name),
    dob: z.date(),
    illness: z.boolean(),
    soleCaregiver: z.boolean(),
  })
}

type ChildcareForm = z.infer<ReturnType<typeof useChildcareSchema>>

const DUPLICATE_NAME_CONSTRAINT = 'users_userchild_user_id_name'

interface Props {
  userId: string
  child?: UserChildNode
}

export function ChildcareDialog({ userId, child }: Props) {
  const translations = useTranslate({
    addChild: 'users.profile.childcare.add-child',
    editChild: 'users.profile.childcare.edit-child',

    form: {
      name: 'users.profile.childcare.form.name',
      nameDescription: 'users.profile.childcare.form.name-description',
      dob: 'users.profile.childcare.form.dob',
      illness: 'users.profile.childcare.form.illness',
      illnessDescription: [
        'users.profile.childcare.form.illness-description',
        { link: <a /> },
      ],
      illnessReadMore: 'users.profile.childcare.form.illness-read-more',
      soleCaregiver: 'users.profile.childcare.form.sole-caregiver',
      soleCaregiverDescription:
        'users.profile.childcare.form.sole-caregiver-description',
    },

    errors: {
      duplicateName: 'users.profile.childcare.errors.duplicate-name',
      couldNotSave: 'users.profile.childcare.errors.could-not-save',
    },
  })

  const dialogRef = useRef<FormDialogRef>(null)
  const [error, setError] = useState<string | null>(null)

  const form = useForm({
    resolver: zodResolver(useChildcareSchema()),
    defaultValues: {
      name: child?.name || '',
      dob: child ? new Date(child.dob) : new Date(),
      illness: child?.illness || false,
      soleCaregiver: child?.soleCaregiver || false,
    },
    mode: 'onBlur',
  })

  const [createChild] = useMutation(CREATE_USER_CHILD_MUTATION, {
    refetchQueries: [{ query: USER_CHILDREN_QUERY, variables: { userId } }],
  })

  const [updateChild] = useMutation(UPDATE_USER_CHILD_MUTATION, {
    refetchQueries: [{ query: USER_CHILDREN_QUERY, variables: { userId } }],
  })

  function resetForm() {
    setTimeout(() => {
      form.reset()
      setError(null)
    }, 300)
  }

  async function handleSubmit(values: ChildcareForm) {
    setError(null)
    const input = {
      user: userId,
      ...values,
      dob: dateFormat(values.dob),
    }

    try {
      if (!child) await createChild({ variables: { input } })
      else await updateChild({ variables: { id: child.id, input } })
    } catch (mutationError) {
      setError(
        (mutationError as Error).message.includes(DUPLICATE_NAME_CONSTRAINT)
          ? translations.errors.duplicateName
          : translations.errors.couldNotSave
      )
      return
    }

    dialogRef.current?.close()
  }

  return (
    <FormDialog
      ref={dialogRef}
      form={form}
      error={error}
      trigger={
        !child ? (
          <Button size="sm">
            <FAIcon icon="plus" />
            {translations.addChild}
          </Button>
        ) : (
          <Button variant="ghost" size="icon-sm">
            <FAIcon icon="edit" />
          </Button>
        )
      }
      header={!child ? translations.addChild : translations.editChild}
      onClose={resetForm}
      onSubmit={handleSubmit}
    >
      <FieldGroup>
        <Controller
          control={form.control}
          name="name"
          render={({ field, fieldState }) => (
            <Field aria-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="name">{translations.form.name}</FieldLabel>

              <FieldDescription>
                {translations.form.nameDescription}
              </FieldDescription>

              <Input {...field} id="name" error={fieldState.error?.message} />
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="dob"
          render={({ field }) => (
            <Field>
              <FieldLabel htmlFor="dob">{translations.form.dob}</FieldLabel>

              <DatePicker {...field} id="dob" mode="single" />
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="illness"
          render={({ field }) => (
            <Field orientation="horizontal">
              <Checkbox {...field} id="illness" />

              <div>
                <FieldLabel htmlFor="illness">
                  {translations.form.illness}
                </FieldLabel>

                <FieldDescription>
                  {translations.form.illnessDescription({
                    link: (
                      <a
                        key="illness-link"
                        href="https://www.arbeidstilsynet.no/lonn-og-ansettelse/permisjoner/rett-til-permisjon-ved-barns-og-barnepassers-sykdom/"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {translations.form.illnessReadMore}
                      </a>
                    ),
                  })}
                </FieldDescription>
              </div>
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="soleCaregiver"
          render={({ field }) => (
            <Field orientation="horizontal">
              <Checkbox {...field} id="sole-caregiver" />

              <div>
                <FieldLabel htmlFor="sole-caregiver">
                  {translations.form.soleCaregiver}
                </FieldLabel>

                <FieldDescription>
                  {translations.form.soleCaregiverDescription}
                </FieldDescription>
              </div>
            </Field>
          )}
        />
      </FieldGroup>
    </FormDialog>
  )
}
